import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Pencil, Plus, Save, Trash2, Package as PackageIcon, EyeOff, Eye, X, ImagePlus, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { broadcastCmsUpdate } from '../../hooks/useCmsContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (url) => (!url ? null : url.startsWith('http') ? url : `${API_URL}${url}`);

const blank = () => ({
  id: '',
  name: '',
  description: '',
  price: 449,
  guests: 8,
  max_guests: 8,
  extra_guest_price: 0,
  duration: '120 minutes',
  features: [''],
  image_url: '',
  active: true,
  sort_order: 99,
});

export const CrmPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/packages`, { withCredentials: true });
      setPackages(r.data || []);
    } catch {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (pkg) => setEditing({ ...pkg, features: [...(pkg.features || [])] });
  const openCreate = () => { setCreating(true); setEditing(blank()); };
  const close = () => { setEditing(null); setCreating(false); };

  const updateField = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const setFeature = (idx, val) => setEditing((p) => {
    const f = [...(p.features || [])];
    f[idx] = val;
    return { ...p, features: f };
  });
  const addFeature = () => setEditing((p) => ({ ...p, features: [...(p.features || []), ''] }));
  const removeFeature = (idx) => setEditing((p) => ({
    ...p,
    features: (p.features || []).filter((_, i) => i !== idx),
  }));

  const uploadImage = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp|gif)$/i.test(file.name)) { toast.error('Use a JPG, PNG, WebP or GIF image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API_URL}/api/admin/cms/upload-image`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('image_url', res.data.url);
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) { toast.error('Name is required'); return; }
    if (Number(editing.price) < 0) { toast.error('Price must be ≥ 0'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description || '',
        price: Number(editing.price),
        max_guests: Number(editing.max_guests || editing.guests),
        guests: Number(editing.max_guests || editing.guests),
        extra_guest_price: Number(editing.extra_guest_price || 0),
        duration: editing.duration || '120 minutes',
        features: (editing.features || []).filter((f) => f && f.trim()),
        image_url: editing.image_url || '',
        active: !!editing.active,
        sort_order: Number(editing.sort_order || 99),
      };
      if (creating) {
        await axios.post(`${API_URL}/api/admin/packages`, payload, { withCredentials: true });
        toast.success('Package created');
      } else {
        await axios.patch(`${API_URL}/api/admin/packages/${editing.id}`, payload, { withCredentials: true });
        toast.success('Package updated — customer page will reflect immediately');
      }
      broadcastCmsUpdate();
      close();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pkg) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/packages/${pkg.id}`,
        { active: !pkg.active },
        { withCredentials: true },
      );
      toast.success(pkg.active ? 'Package hidden from customers' : 'Package activated');
      broadcastCmsUpdate();
      await load();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Hide "${pkg.name}" from customers? Existing bookings will be untouched and you can re-activate later.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/packages/${pkg.id}`, { withCredentials: true });
      toast.success('Package archived (set inactive)');
      broadcastCmsUpdate();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    }
  };

  return (
    <div className="space-y-6" data-testid="crm-packages-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Packages</h1>
          <p className="text-sm text-[#6B6B7A] mt-1">
            Edit name, price, included items and toggle visibility. Existing bookings keep their original snapshot — only new bookings see your changes.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white rounded-full"
          data-testid="package-add-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Package
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6B6B7A]">Loading…</div>
      ) : packages.length === 0 ? (
        <div className="text-center py-12 text-[#6B6B7A]">No packages yet. Click "Add Package" to create the first one.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {packages.map((pkg) => (
            <motion.div
              key={pkg.id}
              layout
              className={`rounded-2xl border-2 p-5 bg-white transition-all ${
                pkg.active ? 'border-[#E8E5DF] hover:border-[#FF3B6B]/40' : 'border-dashed border-[#D5D2CC] opacity-70'
              }`}
              data-testid={`package-card-${pkg.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {imgSrc(pkg.image_url)
                    ? <img src={imgSrc(pkg.image_url)} alt={pkg.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0 border border-[#E8E5DF]" />
                    : <PackageIcon className="w-5 h-5 text-[#FF3B6B] flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1E1E24] truncate">{pkg.name}</p>
                    <p className="text-[11px] text-[#6B6B7A] font-mono">{pkg.id}</p>
                  </div>
                </div>
                {!pkg.active && (
                  <span className="px-2 py-0.5 rounded-full bg-[#F4F1EC] text-[10px] font-semibold text-[#6B6B7A]">
                    INACTIVE
                  </span>
                )}
              </div>
              <p className="text-sm text-[#4d4d57] mt-2 line-clamp-2 min-h-[40px]">{pkg.description || 'No description'}</p>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div className="rounded-lg bg-[#FFF4D6]/40 px-2 py-1.5">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">Price</p>
                  <p className="font-bold text-[#1E1E24]">${Number(pkg.price).toFixed(2)}</p>
                </div>
                <div className="rounded-lg bg-[#FFE9EE]/60 px-2 py-1.5">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">Max guests</p>
                  <p className="font-bold text-[#1E1E24]">{pkg.max_guests || pkg.guests}</p>
                </div>
                <div className="rounded-lg bg-[#E8F5F0]/60 px-2 py-1.5">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">Extra/kid</p>
                  <p className="font-bold text-[#1E1E24]">${Number(pkg.extra_guest_price || 0).toFixed(2)}</p>
                </div>
              </div>
              <div className="mt-3">
                <p className="text-[11px] text-[#6B6B7A] mb-1">Includes ({(pkg.features || []).length})</p>
                <ul className="text-xs text-[#1E1E24] space-y-0.5 max-h-24 overflow-y-auto">
                  {(pkg.features || []).slice(0, 4).map((f, i) => (
                    <li key={`${pkg.id || pkg.package_id}-feat-${i}-${f}`} className="truncate">• {f}</li>
                  ))}
                  {(pkg.features || []).length > 4 && (
                    <li className="text-[#6B6B7A] italic">+ {pkg.features.length - 4} more</li>
                  )}
                </ul>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => openEdit(pkg)}
                  data-testid={`package-edit-${pkg.id}`}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => toggleActive(pkg)}
                  data-testid={`package-toggle-${pkg.id}`}
                  title={pkg.active ? 'Hide from customers' : 'Show to customers'}
                >
                  {pkg.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full text-red-600 hover:text-red-700"
                  onClick={() => handleDelete(pkg)}
                  data-testid={`package-archive-${pkg.id}`}
                  title="Archive (soft-delete)"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit / Create modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent
          className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto"
          data-testid="package-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>
              {creating ? 'Add new package' : `Edit "${editing?.name}"`}
            </DialogTitle>
            <DialogDescription>
              Pricing changes apply only to NEW bookings. Existing bookings keep the package as it was when they booked.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Display Name *</Label>
                  <Input
                    value={editing.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g. Deluxe Fun Creator"
                    data-testid="package-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Sort order</Label>
                  <Input
                    type="number"
                    value={editing.sort_order}
                    onChange={(e) => updateField('sort_order', e.target.value)}
                    data-testid="package-sortorder-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={editing.description || ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={2}
                  placeholder="One or two sentences shown on the package card"
                  data-testid="package-desc-input"
                />
              </div>

              {/* Package photo */}
              <div>
                <Label className="text-xs">Package photo</Label>
                <p className="text-[11px] text-[#6B6B7A] mb-1.5">Shown on the landing page package card (both Compact and Full designs). JPG/PNG/WebP, up to 5MB.</p>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-xl border border-[#E8E5DF] bg-[#FFF4E6] overflow-hidden flex items-center justify-center flex-none" data-testid="package-image-preview">
                    {imgSrc(editing.image_url)
                      ? <img src={imgSrc(editing.image_url)} alt="Package" className="w-full h-full object-cover" />
                      : <ImageIcon className="w-7 h-7 text-[#FB923C]" />}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="package-image-input" />
                    <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="package-image-upload">
                      <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> {uploading ? 'Uploading…' : (editing.image_url ? 'Replace photo' : 'Upload photo')}
                    </Button>
                    {editing.image_url && (
                      <Button type="button" variant="ghost" size="sm" className="rounded-full text-red-600 hover:text-red-700" onClick={() => updateField('image_url', '')} data-testid="package-image-remove">
                        <X className="w-3.5 h-3.5 mr-1.5" /> Remove photo
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Base Price (CAD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editing.price}
                    onChange={(e) => updateField('price', e.target.value)}
                    data-testid="package-price-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Base Guests</Label>
                  <Input
                    type="number"
                    value={editing.guests}
                    onChange={(e) => updateField('guests', e.target.value)}
                    data-testid="package-guests-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Guests</Label>
                  <Input
                    type="number"
                    value={editing.max_guests}
                    onChange={(e) => updateField('max_guests', e.target.value)}
                    data-testid="package-maxguests-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Extra guest $</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editing.extra_guest_price}
                    onChange={(e) => updateField('extra_guest_price', e.target.value)}
                    data-testid="package-extra-price-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Included items</Label>
                <p className="text-[11px] text-[#6B6B7A] mb-1.5">Each item shows as a bullet on the customer's package card.</p>
                <div className="space-y-2">
                  {/* Index-as-key is intentional here: features are plain
                      strings without IDs and this is a controlled-input
                      list. Using string-as-key would break focus when the
                      user is mid-edit on a duplicate value. */}
                  {(editing.features || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={f}
                        onChange={(e) => setFeature(i, e.target.value)}
                        placeholder="e.g. DIY Edible Fruit Bouquet"
                        data-testid={`package-feature-${i}`}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => removeFeature(i)}
                        type="button"
                        data-testid={`package-feature-remove-${i}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addFeature}
                    type="button"
                    className="rounded-full"
                    data-testid="package-feature-add"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add item
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <div>
                  <Label className="text-xs">Active</Label>
                  <p className="text-[11px] text-[#6B6B7A]">When off, customers can't book this package.</p>
                </div>
                <Switch
                  checked={!!editing.active}
                  onCheckedChange={(v) => updateField('active', v)}
                  data-testid="package-active-toggle"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} className="rounded-full">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white rounded-full"
              data-testid="package-save-btn"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? 'Saving…' : (creating ? 'Create package' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmPackages;
